import { Injectable, signal } from '@angular/core';
import { Camera, CameraResultType, Photo } from '@capacitor/camera';
import { v4 as uuid } from 'uuid';
import { ToastService } from '@examdojo/core/toast';
import { ChatInputPhoto } from '../models/chat-image.model';

@Injectable()
export class ChatImageService {
  constructor(private readonly toastService: ToastService) {}

  readonly imageList = signal<ChatInputPhoto[]>([]);

  private readonly maxImageSizeInMb = 3; // 3MB

  async takePicture(): Promise<null | Photo> {
    try {
      return await Camera.getPhoto({
        quality: 100,
        allowEditing: true,
        resultType: CameraResultType.Base64,
      });
    } catch (error) {
      return null;
    }
  }

  async processFiles(files: File | File[]): Promise<void> {
    const fileArray = Array.isArray(files) ? files : [files];
    await Promise.all(fileArray.map((file) => this.addImageToList(file)));
  }

  removeImage(id: string) {
    this.imageList.update((list) => list.filter((photo) => photo.id !== id));
  }

  clearImages() {
    this.imageList.set([]);
  }

  getImages(): string[] {
    return this.imageList().map((image) => image.dataUrl);
  }

  isImageSizeValid(base64String: string): boolean {
    const maxSizeInBytes = this.maxImageSizeInMb * 1024 * 1024; // 3MB
    const actualSizeInBytes = (base64String.length * 3) / 4;
    return actualSizeInBytes < maxSizeInBytes;
  }

  private async addImageToList(file: File): Promise<void> {
    if (!file.type.startsWith('image/')) {
      this.toastService.error(`Only images are allowed. ${file.name} is not an image.`);
      return;
    }

    try {
      const base64String = await this.fileToBase64(file);
      if (!base64String) {
        this.toastService.error(`Failed to process ${file.name}`);
        return;
      }

      if (!this.isImageSizeValid(base64String)) {
        this.toastService.error(`${file.name}: Image size must be less than ${this.maxImageSizeInMb}MB`);
        return;
      }

      this.addBase64ImageToList(base64String);
    } catch {
      this.toastService.error(`Failed to process ${file.name}`);
    }
  }

  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  private addBase64ImageToList(base64String: string): void {
    const chatInputPhoto: ChatInputPhoto = {
      id: uuid(),
      dataUrl: base64String,
    };

    this.imageList.update((list) => [...list, chatInputPhoto]);
  }
}
